import type { DataOption } from '@/entities/column'
import type { FieldSchemaModel } from '@/shared/generated'
import type { JSONSchemaType } from 'ajv'

import { useMemo } from 'react'
import { any, prop, values } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useColumnsSchemaQuery } from '@/entities/column'
import {
	useCompetitorsOptionsQuery,
	useSettingsSchemasQuery,
} from '@/entities/core'
import {
	getAnchoringBehaviorChoices,
	getAnchoringTypeChoices,
} from '@/entities/pricing-campaign'

import { getLocalization } from '@/shared/components/filter/localization'
import { Operators } from '@/shared/components/query-builder'
import { HiddenOnContext } from '@/shared/generated'
import {
	getEnumOptions,
	getResolvedSchema,
	translateOptions,
} from '@/shared/tools/json-schema-utils'

import { selectCompetitorsAsOptions } from './helpers'

export function useSettingsTemplateInterpretationProps({
	type,
}: {
	type: string
}) {
	const { t } = useTranslation()
	const localization = getLocalization(t)

	const schemaQuery = useSettingsSchemasQuery<JSONSchemaType<any>>({
		select: getResolvedSchema(`template:${type}`),
	})
	const columnsQuery = useColumnsSchemaQuery(HiddenOnContext.Assortment, {
		select: (columns) =>
			columns.map(({ name, id }: FieldSchemaModel) => ({
				value: id,
				label: t(name, { ns: 'fields', defaultValue: name }),
			})),
	})
	const competitorsQuery = useCompetitorsOptionsQuery<DataOption[]>(
		{ limit: 0, order_by: 'name' },
		{
			select: selectCompetitorsAsOptions,
		}
	)
	const targetOptions = translateOptions(
		getEnumOptions(
			prop(
				'properties.body.properties.settings.properties.strategy.properties.demand_strategy.properties.target',
				schemaQuery.data
			) ||
				prop(
					'properties.body.properties.strategy.properties.demand_strategy.properties.target',
					schemaQuery.data
				)
		),
		t
	)
	const protectOptions = translateOptions(
		getEnumOptions(
			prop(
				'properties.body.properties.settings.properties.strategy.properties.demand_strategy.properties.protect',
				schemaQuery.data
			) ||
				prop(
					'properties.body.properties.strategy.properties.demand_strategy.properties.protect',
					schemaQuery.data
				)
		),
		t
	)
	const markdownOptions = translateOptions(
		getEnumOptions(
			prop(
				'properties.body.properties.settings.properties.strategy.properties.markdown_strategy.properties.target',
				schemaQuery.data
			) ||
				prop(
					'properties.body.properties.strategy.properties.markdown_strategy.properties.target',
					schemaQuery.data
				)
		),
		t
	)
	// `part:tactics_rb` declares `params.oneOf` directly — one branch per system
	// rule plus a user-rule branch that has no `rule_id` at all, hence the filter.
	// (It used to be nested under `params.allOf[0]` via a `$ref`.)
	const ruleOptions = translateOptions(
		(
			prop(
				'properties.body.properties.settings.properties.pricing_tactics.properties.params.oneOf',
				schemaQuery.data
			) ||
			prop(
				'properties.body.properties.pricing_tactics.properties.params.oneOf',
				schemaQuery.data
			)
		)
			?.filter((rule: any) => rule?.properties?.rule_id?.const)
			.map((rule: any) => ({
				value: rule.properties.rule_id.const,
				label:
					rule.properties.rule_params?.title ||
					`${rule.properties.rule_id.const}_title`,
			})) || [],
		t
	)
	const uiSchema = useMemo(() => {
		const price_limits = {
			lower: {
				name: {
					'ui:options': columnsQuery.data || [],
				},
			},
			upper: {
				name: {
					'ui:options': columnsQuery.data || [],
				},
			},
		}
		const pricing_tactics = {
			params: {
				rule_id: {
					'ui:options': ruleOptions || [],
				},
				rule_params: {
					other_competitors: {
						'ui:options': competitorsQuery.data || [],
					},
					main_competitors: {
						'ui:options': competitorsQuery.data || [],
					},
					column: {
						'ui:options': columnsQuery.data || [],
					},
				},
			},
		}

		const strategy = {
			demand_strategy: {
				target: {
					'ui:options': targetOptions,
				},
				protect: {
					'ui:options': protectOptions,
				},
			},
			markdown_strategy: {
				target: {
					'ui:options': markdownOptions,
				},
			},
		}
		const stop_list = {
			custom_rules: {
				name: {
					'ui:options': columnsQuery.data || [],
				},
				operation: {
					'ui:options': values(Operators).map((value) => ({
						value,
						label: localization.getOperatorLabel(value),
					})),
				},
			},
		}
		const anchoring = {
			rules: {
				anchoring: {
					'ui:options': getAnchoringTypeChoices(t),
				},
				high_range_behavior: {
					'ui:options': getAnchoringBehaviorChoices(t),
				},
				low_range_behavior: {
					'ui:options': getAnchoringBehaviorChoices(t),
				},
			},
			on_conflict: {
				'ui:options': getEnumOptions(
					prop(
						'properties.body.properties.settings.properties.anchoring.properties.on_conflict',
						schemaQuery.data
					)
				),
			},
			on_conflict_anchor_limit: {
				'ui:options': getEnumOptions(
					prop(
						'properties.body.properties.settings.properties.anchoring.properties.on_conflict_anchor_limits',
						schemaQuery.data
					)
				),
			},
			on_anchor_locked: {
				'ui:options': getEnumOptions(
					prop(
						'properties.body.properties.settings.properties.anchoring.properties.on_anchor_locked',
						schemaQuery.data
					)
				),
			},
		}
		const product_assignments = {
			filters: {
				name: {
					'ui:options': columnsQuery.data || [],
				},
				operation: {
					'ui:options': values(Operators).map((value) => ({
						value,
						label: localization.getOperatorLabel(value),
					})),
				},
			},
		}

		return {
			price_limits,
			pricing_tactics,
			strategy,
			stop_list,
			anchoring,
			product_assignments,
			settings: {
				strategy,
				price_limits,
				pricing_tactics,
				stop_list,
				anchoring,
				product_assignments,
			},
		}
	}, [columnsQuery.data, competitorsQuery.data, schemaQuery.data])
	const isLoading = any(prop('isLoading'), [
		competitorsQuery,
		columnsQuery,
		schemaQuery,
	])

	return {
		isLoading,
		uiSchema,
		schema: schemaQuery.data,
	}
}
